import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  fmtTRY, fmtDate,
  JOB_STATUS_LABEL, PAYMENT_STATUS_LABEL,
  CASH_ACCOUNT_LABEL, PAYMENT_METHOD_LABEL,
} from "@/lib/format";
import { PageHeader, SectionCard, StatCard, EmptyState, StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Link2, Check, X } from "lucide-react";
import { ArrowLeft, Plus, Pencil, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";
import PaymentsDialog from "@/components/PaymentsDialog";
import JobFormDialog from "@/components/JobFormDialog";
import { generateCustomerStatementPdf } from "@/lib/pdfStatement";

/**
 * Inline job assignment cell for the "Müşteriye Atanan Satın Almalar"
 * table.  When ``purchase.job_id`` is set we show the bound job name; when
 * null we expose a compact "İşe Bağla" dropdown that posts to
 * ``PATCH /purchases/{id}/assign-job`` with only the job_id — leaving
 * customer_id, payments, invoice_status, VAT untouched.
 *
 * UX notes:
 *   • Dropdown seçenekleri "İş Adı — DD.MM.YYYY — kısa açıklama" formatında.
 *   • Seçilen işte zaten BAŞKA satın almalar varsa kaydetmeden önce
 *     bir onay diyaloğu açılır (yanlış seçim koruması; birden fazla
 *     satın almayı aynı işe bağlamak her zaman mümkündür).
 */
function AssignJobCell({ purchase, customerJobs, customerPurchases, onAssigned }) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const boundJob = (customerJobs ?? []).find((j) => j.id === purchase.job_id);
  const boundName = purchase.job_name || boundJob?.name;

  // Seçilen iş'e zaten bağlı DİĞER satın almalar (bu kayıt hariç).
  const existingOnSelected = (customerPurchases ?? []).filter(
    (p) => p.job_id && p.job_id === selected && p.id !== purchase.id
  );

  const formatJobOption = (j) => {
    const d = j.start_date ? fmtDate(j.start_date) : "—";
    const desc = (j.description || "").trim();
    const short = desc.length > 40 ? desc.slice(0, 40) + "…" : desc;
    return short ? `${j.name} — ${d} — ${short}` : `${j.name} — ${d}`;
  };

  if (purchase.job_id && !editing) {
    return (
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1 text-xs font-medium text-slate-700"
          data-testid={`purchase-job-name-${purchase.id}`}
        >
          <Link2 className="w-3 h-3 text-slate-400" />
          {boundName || "—"}
        </span>
        <Button
          size="sm" variant="ghost"
          className="h-6 px-1.5 text-[11px] text-slate-500 hover:text-slate-900"
          onClick={() => { setSelected(purchase.job_id); setEditing(true); }}
          data-testid={`purchase-change-job-${purchase.id}`}
        >
          değiştir
        </Button>
      </div>
    );
  }

  if (!editing) {
    return (
      <Button
        size="sm" variant="outline"
        className="h-8 rounded-lg text-xs"
        onClick={() => { setSelected(""); setEditing(true); }}
        data-testid={`purchase-assign-job-${purchase.id}`}
      >
        <Link2 className="w-3.5 h-3.5 mr-1" /> İşe Bağla
      </Button>
    );
  }

  const doPatch = async () => {
    setSaving(true);
    try {
      await api.patch(`/purchases/${purchase.id}/assign-job`, {
        job_id: selected || null,
      });
      toast.success(selected ? "Satın alma işe bağlandı" : "İş bağlantısı kaldırıldı");
      setEditing(false);
      setConfirmOpen(false);
      await onAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncelleme başarısız");
    } finally { setSaving(false); }
  };

  const handleSave = () => {
    // Boşa alma veya başka hiçbir satın alma yoksa doğrudan kaydet.
    // "— Yok —" seçildiyse veya ilk kez bağlanıyorsa onay sorulmaz.
    if (!selected || existingOnSelected.length === 0) {
      doPatch();
      return;
    }
    setConfirmOpen(true);
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <Select value={selected || "none"} onValueChange={(v) => setSelected(v === "none" ? "" : v)}>
          <SelectTrigger
            className="h-8 min-w-[260px] rounded-lg text-xs"
            data-testid={`purchase-job-select-${purchase.id}`}
          >
            <SelectValue placeholder="İş seçin" />
          </SelectTrigger>
          <SelectContent className="max-w-[480px]">
            <SelectItem value="none">— Yok (genel) —</SelectItem>
            {(customerJobs ?? []).map((j) => (
              <SelectItem key={j.id} value={j.id}>{formatJobOption(j)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="icon" variant="ghost" className="h-8 w-8 text-emerald-600"
          onClick={handleSave} disabled={saving}
          data-testid={`purchase-job-save-${purchase.id}`}
        >
          <Check className="w-4 h-4" />
        </Button>
        <Button
          size="icon" variant="ghost" className="h-8 w-8 text-slate-500"
          onClick={() => { setEditing(false); setConfirmOpen(false); }}
          disabled={saving}
          data-testid={`purchase-job-cancel-${purchase.id}`}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid={`purchase-job-confirm-${purchase.id}`}>
          <DialogHeader>
            <DialogTitle>Bu işe zaten bağlı satın alma var</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-700 leading-relaxed">
              Bu işe zaten <strong>{existingOnSelected.length} satın alma</strong> bağlı.
              Bu satın almayı da aynı işe bağlamak istiyor musunuz?
            </p>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 divide-y divide-slate-200">
              {existingOnSelected.slice(0, 8).map((p) => (
                <div key={p.id} className="px-3 py-2 flex items-center justify-between gap-3 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.vendor_name || "—"}</div>
                    <div className="text-slate-600 truncate">{p.description || "—"}</div>
                  </div>
                  <div className="tabular-nums text-slate-700 font-semibold whitespace-nowrap">
                    {fmtTRY(Number(p.amount) || 0)}
                  </div>
                </div>
              ))}
              {existingOnSelected.length > 8 && (
                <div className="px-3 py-1.5 text-[11px] text-slate-500">
                  +{existingOnSelected.length - 8} daha…
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={saving}
              data-testid={`purchase-job-confirm-cancel-${purchase.id}`}
            >
              Vazgeç
            </Button>
            <Button
              onClick={doPatch}
              disabled={saving}
              data-testid={`purchase-job-confirm-ok-${purchase.id}`}
            >
              Evet, bağla
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [jobOpen, setJobOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [paymentsFor, setPaymentsFor] = useState(null);
  const [notesValue, setNotesValue] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers/${id}`);
      setData(data);
      setNotesValue(data.notes || "");
    } catch {
      navigate("/customers");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-slate-500">Yükleniyor…</div>;

  const onJobEdit = (j) => { setEditingJob(j); setJobOpen(true); };

  const onJobDelete = async (j) => {
    if (!window.confirm(`"${j.name}" işi silinsin mi? Tüm tahsilatlar da silinecek.`)) return;
    await api.delete(`/jobs/${j.id}`);
    toast.success("İş silindi");
    load();
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/customers/${id}/notes`, { notes: notesValue });
      toast.success("Notlar güncellendi");
      load();
    } catch {
      toast.error("Notlar kaydedilemedi");
    } finally { setSavingNotes(false); }
  };

  const toggleStatus = async () => {
    const next = data.status === "passive" ? "active" : "passive";
    const label = next === "passive" ? "pasife" : "aktife";
    if (!window.confirm(`Bu müşteriyi ${label} almak istediğinize emin misiniz? Geçmiş veriler korunacak.`)) return;
    try {
      await api.put(`/customers/${id}/status`, { status: next });
      toast.success(next === "passive" ? "Müşteri pasife alındı" : "Müşteri yeniden aktif");
      load();
    } catch {
      toast.error("Durum güncellenemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/customers")}
          className="mb-3 -ml-2 text-slate-600" data-testid="back-to-customers">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Müşterilere Dön
        </Button>
        <div className="flex items-start justify-between gap-4">
          <PageHeader title={data.name} subtitle={data.company_name || "Müşteri Cari Kartı"} />
          <div className="flex items-center gap-2 shrink-0 pt-1">
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                data.status === "active"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-slate-100 text-slate-600 border-slate-200"
              }`}
              data-testid="customer-status-badge"
            >
              {data.status === "active" ? "Aktif" : "Pasif"}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="rounded-lg"
              onClick={toggleStatus}
              data-testid="customer-status-toggle"
            >
              {data.status === "active" ? "Pasife Al" : "Aktife Al"}
            </Button>
          </div>
        </div>
      </div>

      {data.status === "passive" && (
        <div className="px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm" data-testid="passive-banner">
          Bu müşteri <span className="font-semibold">pasif</span> durumda. Geçmiş işler, tahsilatlar ve raporlar korunmaktadır; yeni işlemler eklemeden önce müşteriyi tekrar aktif yapın.
        </div>
      )}

      {/* Cari kart üst bilgileri */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="İletişim Bilgileri" className="lg:col-span-1">
          <dl className="px-5 py-4 space-y-3 text-sm">
            <Row label="Yetkili" value={data.contact_person || "—"} />
            <Row label="Telefon" value={data.phone || "—"} />
            <Row label="E-posta" value={data.email || "—"} />
            {data.tax_office && <Row label="Vergi Dairesi" value={data.tax_office} />}
            {data.tax_number && <Row label="Vergi / T.C. Kimlik No" value={data.tax_number} />}
            <Row label="Adres" value={data.address || "—"} />
          </dl>
        </SectionCard>

        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="Toplam Satış" value={fmtTRY(data.total_sales)} accent="slate" testId="cust-total-sales" />
          <StatCard label="Tahsil Edilen" value={fmtTRY(data.total_collected)} accent="emerald" />
          <StatCard label="Bekleyen Alacak" value={fmtTRY(data.outstanding)} accent={data.outstanding > 0 ? "amber" : "slate"} testId="cust-outstanding" />
          <StatCard label="İş Sayısı" value={data.job_count} accent="slate" />
          <StatCard label="İlk İş" value={data.first_job_date ? fmtDate(data.first_job_date) : "—"} accent="slate" />
          <StatCard label="Son İş" value={data.last_job_date ? fmtDate(data.last_job_date) : "—"} accent="slate" />
        </div>
      </div>

      <Tabs defaultValue="jobs" className="space-y-4">
        <TabsList className="bg-white border border-slate-200 rounded-lg p-1 h-auto">
          <TabsTrigger value="jobs" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-jobs">İşler / Satışlar</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-payments">Tahsilatlar</TabsTrigger>
          <TabsTrigger value="movements" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-movements">Cari Hareketler</TabsTrigger>
          <TabsTrigger value="notes" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-notes">Notlar</TabsTrigger>
          <TabsTrigger value="profit" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-profit">Karlılık</TabsTrigger>
        </TabsList>

        {/* TAB 1: Jobs */}
        <TabsContent value="jobs" className="space-y-4">
          <div className="flex justify-end">
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-lg"
                    onClick={() => { setEditingJob(null); setJobOpen(true); }}
                    data-testid="add-job-button">
              <Plus className="w-4 h-4 mr-1.5" /> Yeni İş
            </Button>
          </div>

          <JobFormDialog
            open={jobOpen}
            onOpenChange={(v) => { setJobOpen(v); if (!v) setEditingJob(null); }}
            editing={editingJob}
            lockedCustomer={data}
            onSaved={load}
          />

          <SectionCard testId="customer-jobs-card">
            {(!data.jobs || (data.jobs ?? []).length === 0) ? (
              <EmptyState title="Bu müşteri için iş yok" hint="Yeni İş butonu ile başlayın." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/50">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">İş</th>
                      <th className="px-5 py-3 font-semibold">Tarihler</th>
                      <th className="px-5 py-3 font-semibold">Durum</th>
                      <th className="px-5 py-3 font-semibold text-right">Satış Bedeli</th>
                      <th className="px-5 py-3 font-semibold text-right">Satış KDV'si</th>
                      <th className="px-5 py-3 font-semibold text-right">Tahsil</th>
                      <th className="px-5 py-3 font-semibold text-right">Kalan</th>
                      <th className="px-5 py-3 font-semibold text-right">Alım Bedeli</th>
                      <th className="px-5 py-3 font-semibold text-right">Alım KDV'si</th>
                      <th className="px-5 py-3 font-semibold text-right">Net Kâr</th>
                      <th className="px-5 py-3 font-semibold">Ödeme</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(data.jobs ?? []).map((j) => {
                      // Satış Bedeli = KDV hariç net iş bedeli (``job.amount``).
                      // Satış KDV'si = Faturalı iş için SUM(invoice.grand_total)
                      // - job.amount; fatura yoksa veya faturasız kayıtta 0.
                      // GROSS = NET + KDV (asla sadece grand_total_sum
                      // üzerinden değil — ``invoice_status="invoiced"``
                      // ama fatura kesilmemişse grand_total_sum=0 olur ve
                      // Kalan negatife düşerdi).
                      const isInvoiced = (j.invoice_status || "invoiced") === "invoiced";
                      const salesNet = Number(j.amount) || 0;
                      const invoiceGrandSum = Number(j.invoice_grand_total_sum) || 0;
                      const salesVat = isInvoiced
                        ? Math.max(0, invoiceGrandSum - salesNet)
                        : 0;
                      const salesGross = salesNet + salesVat;
                      const kalan = salesGross - (Number(j.paid_amount) || 0);
                      // Alımlar — bu işe bağlı satın almaların KDV hariç
                      // net toplamı (``purchase.amount``; faturalı/faturasız
                      // her iki durumda da NET) ve KDV toplamı ayrı
                      // kolonda gösterilir.  KDV, kesilmiş faturalardan
                      // (``invoice_grand_total_sum - amount``) hesaplanır;
                      // fatura kesilmemişse 0.  Backend kârlılık hesabını
                      // etkilemez — yalnızca satır bazında sunum.
                      const rowPurchasesList = (data.purchases ?? []).filter((p) => p.job_id === j.id);
                      const rowPurchasesNet = rowPurchasesList.reduce((s, p) => s + (Number(p.amount) || 0), 0);
                      const rowPurchasesVat = rowPurchasesList.reduce((s, p) => {
                        const pInv = (p.invoice_status || "invoiced") === "invoiced";
                        if (!pInv) return s;
                        const pNet = Number(p.amount) || 0;
                        const pG = Number(p.invoice_grand_total_sum) || 0;
                        return s + Math.max(0, pG - pNet);
                      }, 0);
                      // Net Kâr = KDV hariç satış - KDV hariç alım.
                      // Örnek: 42.000 - 1.000 = 41.000.  KDV tutarları
                      // kâra dahil edilmez (KDV firma tahsil ettiği /
                      // ödediği transit vergi; kârlılığa girmez).
                      const rowNetProfit = salesNet - rowPurchasesNet;
                      return (
                        <tr key={j.id} className="hover:bg-slate-50/60" data-testid={`job-row-${j.id}`}>
                          <td className="px-5 py-3">
                            <button
                              type="button"
                              onClick={() => onJobEdit(j)}
                              className="text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 rounded"
                              data-testid={`job-title-${j.id}`}
                              title="İşi düzenle"
                            >
                              <div className="font-medium text-slate-900 group-hover:underline cursor-pointer">{j.name}</div>
                              <div className="text-xs text-slate-500 truncate max-w-xs">{j.description || "—"}</div>
                            </button>
                          </td>
                          <td className="px-5 py-3 text-xs text-slate-600">
                            {j.start_date ? fmtDate(j.start_date) : "—"}<br />
                            {j.end_date ? fmtDate(j.end_date) : <span className="text-slate-400">Devam</span>}
                          </td>
                          <td className="px-5 py-3"><StatusBadge value={j.status} label={JOB_STATUS_LABEL[j.status]} /></td>
                          <td className="px-5 py-3 text-right tabular-nums"
                              data-testid={`job-sales-net-${j.id}`}>
                            {fmtTRY(salesNet)}
                          </td>
                          <td className="px-5 py-3 text-right tabular-nums text-slate-600"
                              data-testid={`job-sales-vat-${j.id}`}>
                            {fmtTRY(salesVat)}
                          </td>
                          <td className="px-5 py-3 text-right tabular-nums text-emerald-600">{fmtTRY(j.paid_amount)}</td>
                          <td className="px-5 py-3 text-right tabular-nums font-semibold">{fmtTRY(kalan)}</td>
                          <td
                            className="px-5 py-3 text-right tabular-nums text-red-600"
                            data-testid={`job-purchases-net-${j.id}`}
                          >
                            {fmtTRY(rowPurchasesNet)}
                          </td>
                          <td
                            className="px-5 py-3 text-right tabular-nums text-slate-600"
                            data-testid={`job-purchases-vat-${j.id}`}
                          >
                            {fmtTRY(rowPurchasesVat)}
                          </td>
                          <td
                            className={`px-5 py-3 text-right tabular-nums font-semibold ${
                              rowNetProfit >= 0 ? "text-emerald-600" : "text-red-600"
                            }`}
                            data-testid={`job-net-profit-${j.id}`}
                          >
                            {fmtTRY(rowNetProfit)}
                          </td>
                          <td className="px-5 py-3"><StatusBadge value={j.payment_status} label={PAYMENT_STATUS_LABEL[j.payment_status]} /></td>
                          <td className="px-5 py-3">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={() => setPaymentsFor(j)} data-testid={`job-payments-${j.id}`}>Tahsilatlar</Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onJobEdit(j)} data-testid={`job-edit-${j.id}`}><Pencil className="w-4 h-4" /></Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => onJobDelete(j)} data-testid={`job-delete-${j.id}`}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* TAB 2: All payments across jobs */}
        <TabsContent value="payments">
          <SectionCard title={`Tüm Tahsilatlar (${data.payments?.length || 0})`} testId="customer-payments-card">
            {(!data.payments || (data.payments ?? []).length === 0) ? (
              <EmptyState title="Henüz tahsilat kaydı yok" hint="Tahsilatlar İşler sekmesinden eklenir." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/50">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">Tarih</th>
                      <th className="px-5 py-3 font-semibold">İş</th>
                      <th className="px-5 py-3 font-semibold text-right">Tutar</th>
                      <th className="px-5 py-3 font-semibold">Kasa</th>
                      <th className="px-5 py-3 font-semibold">Yöntem</th>
                      <th className="px-5 py-3 font-semibold">Notlar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(data.payments ?? []).map((p) => (
                      <tr key={p.id}>
                        <td className="px-5 py-3 whitespace-nowrap">{fmtDate(p.date)}</td>
                        <td className="px-5 py-3 font-medium">{p.job_name}</td>
                        <td className="px-5 py-3 text-right tabular-nums font-semibold text-emerald-600">{fmtTRY(p.amount)}</td>
                        <td className="px-5 py-3 text-xs">{CASH_ACCOUNT_LABEL[p.cash_account]}</td>
                        <td className="px-5 py-3 text-xs">{PAYMENT_METHOD_LABEL[p.payment_method]}</td>
                        <td className="px-5 py-3 text-xs text-slate-600">{p.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* TAB 3: Cari Hareketler (Account Statement) */}
        <TabsContent value="movements">
          <MovementsTab customer={data} jobs={data.jobs || []} payments={data.payments || []} />
        </TabsContent>

        {/* TAB 4: Notes */}
        <TabsContent value="notes">
          <SectionCard title="Müşteri Notları">
            <div className="p-5 space-y-3">
              <Textarea value={notesValue} onChange={(e) => setNotesValue(e.target.value)} rows={10} className="rounded-lg" placeholder="Müşteri ile ilgili notlarınız..." data-testid="notes-textarea" />
              <div className="flex justify-end">
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={saveNotes} disabled={savingNotes} data-testid="save-notes-button">
                  {savingNotes ? "Kaydediliyor..." : "Notları Kaydet"}
                </Button>
              </div>
            </div>
          </SectionCard>
        </TabsContent>

        {/* TAB 4: Profitability */}
        <TabsContent value="profit" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label="Toplam Satış" value={fmtTRY(data.total_sales)} accent="slate" testId="profit-sales" />
            <StatCard label="Maliyet (Bu Müşteri)" value={fmtTRY(data.total_costs)} accent="red" testId="profit-costs" />
            <StatCard label="Net Kâr" value={fmtTRY(data.net_profit)} accent={data.net_profit >= 0 ? "emerald" : "red"} testId="profit-net" />
            <StatCard label="Kâr Marjı" value={data.total_sales > 0 ? `${((data.net_profit / data.total_sales) * 100).toFixed(1)}%` : "—"} accent="slate" />
          </div>

          <SectionCard title={`Müşteriye Atanan Satın Almalar (${data.purchases?.length || 0})`} testId="customer-purchases-card">
            {(!data.purchases || (data.purchases ?? []).length === 0) ? (
              <EmptyState title="Bu müşteri için satın alma yok" hint="Satın alma kaydı oluştururken müşteri seçilmelidir." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/50">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">Tarih</th>
                      <th className="px-5 py-3 font-semibold">Tedarikçi</th>
                      <th className="px-5 py-3 font-semibold">Açıklama</th>
                      <th className="px-5 py-3 font-semibold">Bağlı İş</th>
                      <th className="px-5 py-3 font-semibold text-right">Alım Bedeli</th>
                      <th className="px-5 py-3 font-semibold text-right">Alım KDV'si</th>
                      <th className="px-5 py-3 font-semibold text-right">Toplam</th>
                      <th className="px-5 py-3 font-semibold">Ödeme</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(data.purchases ?? []).map((p) => {
                      // Alım Bedeli = ``purchase.amount`` (KDV hariç net).
                      // Alım KDV'si = Faturalı için invoice.grand_total - amount;
                      // fatura yoksa veya faturasız kayıtta 0.  Toplam = NET + KDV
                      // (asla sadece grand_total_sum üzerinden değil).
                      const pInvoiced = (p.invoice_status || "invoiced") === "invoiced";
                      const purNet = Number(p.amount) || 0;
                      const purInvGrand = Number(p.invoice_grand_total_sum) || 0;
                      const purVat = pInvoiced
                        ? Math.max(0, purInvGrand - purNet)
                        : 0;
                      const purGross = purNet + purVat;
                      return (
                        <tr key={p.id}>
                          <td className="px-5 py-3 whitespace-nowrap">{fmtDate(p.date)}</td>
                          <td className="px-5 py-3 font-medium">{p.vendor_name}</td>
                          <td className="px-5 py-3 text-slate-700 max-w-xs truncate">{p.description || "—"}</td>
                          <td className="px-5 py-3">
                            <AssignJobCell
                              purchase={p}
                              customerJobs={data.jobs}
                              customerPurchases={data.purchases}
                              onAssigned={load}
                            />
                          </td>
                          <td className="px-5 py-3 text-right tabular-nums"
                              data-testid={`purchase-net-${p.id}`}>
                            {fmtTRY(purNet)}
                          </td>
                          <td className="px-5 py-3 text-right tabular-nums text-slate-600"
                              data-testid={`purchase-vat-${p.id}`}>
                            {fmtTRY(purVat)}
                          </td>
                          <td className="px-5 py-3 text-right tabular-nums font-semibold"
                              data-testid={`purchase-gross-${p.id}`}>
                            {fmtTRY(purGross)}
                          </td>
                          <td className="px-5 py-3"><StatusBadge value={p.payment_status} label={PAYMENT_STATUS_LABEL[p.payment_status]} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>

      <PaymentsDialog
        key={paymentsFor?.id}
        open={!!paymentsFor}
        onOpenChange={(v) => { if (!v) setPaymentsFor(null); }}
        type="job"
        record={paymentsFor}
        onUpdated={() => load()}
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</dt>
      <dd className="col-span-2 text-slate-800 break-words">{value}</dd>
    </div>
  );
}

function MovementsTab({ customer, jobs, payments }) {
  // Build chronological transactions: sales (debit) + collections (credit)
  const txns = [];
  for (const j of jobs) {
    // Faturalı iş için SATIŞ hareketinin BORÇ tutarı KDV DAHİL fatura
    // toplamı olur; ``job.amount`` (KDV hariç iş bedeli) cari hesap
    // hareketi için yanlış bir base'dir.  Backend'in ``get_customer``
    // endpoint'inde display-only olarak eklenen ``invoice_grand_total_sum``
    // alanı burada kullanılır (tedarikçi tarafındaki VendorMovementsTab
    // ile birebir aynı desen).  Faturasız iş için mevcut davranış
    // korunur (``job.amount``).
    const isInvoiced = (j.invoice_status || "invoiced") === "invoiced";
    const debit = isInvoiced
      ? (Number(j.invoice_grand_total_sum) || 0)
      : (Number(j.amount) || 0);
    txns.push({
      key: `j-${j.id}`,
      date: j.start_date || (j.created_at ? j.created_at.slice(0, 10) : ""),
      type: "sale",
      description: j.name + (j.description ? ` — ${j.description}` : ""),
      job_name: j.name,
      debit,
      credit: 0,
      vatBadge: isInvoiced,
    });
  }
  for (const p of payments) {
    txns.push({
      key: `p-${p.id}`,
      date: p.date,
      type: "collection",
      description: p.job_name + (p.notes ? ` — ${p.notes}` : ""),
      job_name: p.job_name,
      debit: 0,
      credit: +p.amount || 0,
    });
  }
  // Stable chronological sort (oldest first). Sales before collections on same day.
  txns.sort((a, b) => {
    const da = a.date || "";
    const db = b.date || "";
    if (da !== db) return da < db ? -1 : 1;
    if (a.type === b.type) return 0;
    return a.type === "sale" ? -1 : 1;
  });

  let running = 0;
  const rows = (txns ?? []).map((t) => {
    running += t.debit - t.credit;
    return { ...t, balance: running };
  });

  const totalDebit = (rows ?? []).reduce((s, r) => s + r.debit, 0);
  const totalCredit = (rows ?? []).reduce((s, r) => s + r.credit, 0);
  const finalBalance = totalDebit - totalCredit;

  const onDownloadPdf = () => {
    if ((rows ?? []).length === 0) {
      toast.error("Henüz cari hareket yok, PDF oluşturulamaz");
      return;
    }
    try {
      generateCustomerStatementPdf({ customer, rows, totalDebit, totalCredit, finalBalance });
      toast.success("PDF cari ekstresi oluşturuldu");
    } catch (err) {
      console.error(err);
      toast.error("PDF oluşturulamadı");
    }
  };

  return (
    <SectionCard
      title={`Cari Hareket Dökümü (${(rows ?? []).length})`}
      testId="customer-movements-card"
      action={
        <Button
          size="sm"
          variant="outline"
          className="h-9 rounded-lg"
          onClick={onDownloadPdf}
          disabled={(rows ?? []).length === 0}
          data-testid="download-statement-pdf-button"
        >
          <FileDown className="w-4 h-4 mr-1.5" /> PDF Cari Ekstresi Oluştur
        </Button>
      }
    >
      {(rows ?? []).length === 0 ? (
        <EmptyState title="Henüz cari hareket yok" hint="İşler ve tahsilatlar bu sekmede kronolojik olarak listelenir." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Tarih</th>
                <th className="px-5 py-3 font-semibold">İşlem Tipi</th>
                <th className="px-5 py-3 font-semibold">Açıklama</th>
                <th className="px-5 py-3 font-semibold text-right">Borç</th>
                <th className="px-5 py-3 font-semibold text-right">Alacak</th>
                <th className="px-5 py-3 font-semibold text-right">Bakiye</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(rows ?? []).map((r) => (
                <tr key={r.key} className="hover:bg-slate-50/60" data-testid={`movement-row-${r.key}`}>
                  <td className="px-5 py-3 whitespace-nowrap">{r.date ? fmtDate(r.date) : "—"}</td>
                  <td className="px-5 py-3">
                    <StatusBadge
                      value={r.type === "sale" ? "invoiced" : "paid"}
                      label={r.type === "sale" ? "Satış" : "Tahsilat"}
                    />
                  </td>
                  <td className="px-5 py-3 text-slate-700 max-w-md truncate">{r.description || "—"}</td>
                  <td className="px-5 py-3 text-right tabular-nums" data-testid={`movement-debit-${r.key}`}>
                    {r.debit > 0 ? (
                      <span className="text-slate-900 inline-flex items-center gap-1.5 justify-end">
                        {fmtTRY(r.debit)}
                        {r.vatBadge && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium
                                       bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider"
                            title="KDV Dahil — Faturaların Genel Toplamı"
                            data-testid={`movement-debit-vat-badge-${r.key}`}
                          >
                            KDV
                          </span>
                        )}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {r.credit > 0 ? <span className="text-emerald-600">{fmtTRY(r.credit)}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className={`px-5 py-3 text-right tabular-nums font-semibold ${r.balance > 0 ? "text-amber-700" : r.balance < 0 ? "text-emerald-700" : "text-slate-500"}`}>
                    {fmtTRY(r.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr className="border-t-2 border-slate-200 font-semibold">
                <td className="px-5 py-3" colSpan={3}>
                  <span className="text-[11px] uppercase tracking-wider text-slate-600">Toplam</span>
                </td>
                <td className="px-5 py-3 text-right tabular-nums text-slate-900" data-testid="movements-total-debit">{fmtTRY(totalDebit)}</td>
                <td className="px-5 py-3 text-right tabular-nums text-emerald-700" data-testid="movements-total-credit">{fmtTRY(totalCredit)}</td>
                <td className={`px-5 py-3 text-right tabular-nums ${finalBalance > 0 ? "text-amber-700" : finalBalance < 0 ? "text-emerald-700" : "text-slate-500"}`} data-testid="movements-final-balance">
                  {fmtTRY(finalBalance)}
                </td>
              </tr>
              <tr className="text-xs text-slate-500">
                <td className="px-5 pb-3" colSpan={6}>
                  Pozitif bakiye: müşterinin size olan borcu (alacak). Negatif bakiye: avans / fazla tahsilat.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
